import  { useState, useEffect } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  Container,
} from "@mui/material";

import headerTitles from "@utils/header";

const Header = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleDrawer = () => setDrawerOpen(!drawerOpen);

  const scrollToElement = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      setDrawerOpen(false);
    }
  };

  // Menu Button - only shows on mobile
  const MenuButton = () => (
    <Box
      onClick={toggleDrawer}
      sx={{
        position: "fixed",
        right: "20px",
        top: "20px",
        zIndex: 2000,
        cursor: "pointer",
        width: "30px",
        height: "30px",
        display: { xs: "flex", md: "none" },
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          width: "24px",
          height: "14px",
          position: "relative",
          transform: drawerOpen ? "rotate(-180deg)" : "rotate(0)",
          transition: "transform 0.3s ease",
        }}
      >
        <Box
          component="span"
          sx={{
            position: "absolute",
            width: "100%",
            height: "1.5px",
            backgroundColor: drawerOpen ? "#fff" : "#000",
            transition: "all 0.3s ease",
            top: 0,
            transform: drawerOpen
              ? "rotate(45deg) translate(4.5px, 4.5px)"
              : "none",
          }}
        />
        <Box
          component="span"
          sx={{
            position: "absolute",
            width: "100%",
            height: "1.5px",
            backgroundColor: drawerOpen ? "#fff" : "#000",
            transition: "all 0.3s ease",
            bottom: 0,
            transform: drawerOpen
              ? "rotate(-45deg) translate(4.5px, -4.5px)"
              : "none",
          }}
        />
      </Box>
    </Box>
  );

  // Desktop Navigation Item
  const DesktopNavItem = ({ item }: { item: string }) => (
    <Box
      onClick={() => scrollToElement(item)}
      sx={{
        cursor: "pointer",
        px: 3,
        py: 1,
        transition: "all 0.2s ease",
        "&:hover": {
          color: "primary.main",
        },
      }}
    >
      <Typography
        sx={{
          fontSize: "0.95rem",
          fontWeight: 500,
        }}
      >
        {item}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          bgcolor: scrolled
            ? "rgba(255, 255, 255, 0.98)"
            : "rgba(255, 255, 255, 0.98)",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Container maxWidth="lg">
          <Toolbar
            sx={{
              justifyContent: "center",
              minHeight: { xs: "60px", md: "70px" },
              position: "relative",
            }}
          >
           

            {/* Desktop Navigation */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                gap: 2,
              }}
            >
              {headerTitles.map((item) => (
                <DesktopNavItem key={item} item={item} />
              ))}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Menu Button - Mobile Only */}
      <MenuButton />

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer}
        variant="temporary"
        sx={{
          display: { md: "none" },
          "& .MuiDrawer-paper": {
            width: "130px",
            height: "100%",
            bgcolor: "#000",
            color: "#fff",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            px: 2,
          },
        }}
      >
        <List>
          {headerTitles.map((item, index) => (
            <ListItem
              key={item}
              onClick={() => scrollToElement(item)}
              sx={{
                py: 2,
                opacity: drawerOpen ? 1 : 0,
                transform: drawerOpen ? "translateX(0)" : "translateX(40px)",
                transition: `all 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${
                  index * 0.1
                }s`,
                cursor: "pointer",
                "&:hover": {
                  "& .nav-text": {
                    transform: "translateX(10px)",
                    color: "rgba(255, 255, 255, 0.7)",
                  },
                  "& .nav-number": {
                    opacity: 1,
                  },
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  className="nav-number"
                  sx={{
                    fontSize: "0.75rem",
                    opacity: 0.5,
                    width: "24px",
                    transition: "all 0.2s ease",
                  }}
                >
                  {String(index + 1).padStart(2, "0")}
                </Typography>
                <Typography
                  className="nav-text"
                  sx={{
                    fontSize: "1.25rem",
                    fontWeight: 300,
                    transition: "all 0.3s ease",
                    ml: 1,
                  }}
                >
                  {item}
                </Typography>
              </Box>
            </ListItem>
          ))}
        </List>
      </Drawer>

      <Toolbar sx={{ minHeight: { xs: "60px", md: "70px" } }} />
    </Box>
  );
};

export default Header;
